import React from "react";
import {
  Alert,
  Box,
  Button,
  Modal,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationPredicate,
  InvestigationSuggestion,
  Stage,
} from "../../lusca-search-api/generated-src";
import { useWidenRule } from "../../hooks/useInvestigations";
import { InvestigationRule } from "./InvestigationRule";
import {
  predicateValueText,
  ruleFieldLabel,
  ruleWithin,
  titleForRule,
  trackStarsTitle,
} from "./investigationUtils";

type Rule = InvestigationPredicate[];

/**
 * The case's rules after widening, as the backend computes them: the new rule is added as an OR
 * branch, a branch it contains is dropped, and nothing changes when a branch already covers it.
 */
export const widenedRules = (current: Rule[], added: Rule): Rule[] => {
  if (current.some((branch) => ruleWithin(added, branch))) return current;
  return [...current.filter((branch) => !ruleWithin(branch, added)), added];
};

/** The case's rule branches (legacy cases only carry `rule`). */
export const caseRules = (investigation: Investigation): Rule[] =>
  investigation.rules?.length
    ? investigation.rules
    : investigation.rule?.length
    ? [investigation.rule]
    : [];

/**
 * The title the case should carry after widening, or undefined to keep the current one. A
 * generated title names the old rule's scope ("… (marketplace=BR)"), so it is regenerated for the
 * widened rule; a title a person typed is theirs and never replaced. Several OR branches have no
 * single generated name, so they keep the title too.
 */
export const widenedTitle = (
  investigation: Investigation,
  after: Rule[],
  category?: string | null
): string | undefined => {
  if (investigation.titleSource === "user" || after.length !== 1)
    return undefined;
  const title = category?.trim()
    ? trackStarsTitle(category, after[0])
    : titleForRule(after[0]);
  return title !== investigation.title ? title : undefined;
};

/** True when widening the case with `rule` would change it. */
export const canWiden = (investigation: Investigation, rule: Rule): boolean =>
  rule.length > 0 &&
  !caseRules(investigation).some((branch) => ruleWithin(rule, branch));

/**
 * Patterns the case's rule could widen to: the pattern's rule holds the case's (a newer report
 * found more of the same failure), widening would change the case, and the pattern's rule is
 * complete. A pattern with an open text constraint ("message contains …") or a clause condition
 * Lusca could not read derives a rule wider than the pattern itself, so widening to it would
 * sweep in unrelated errors — never offered.
 * Most specific first: the tightest rule is the one closest to what the case already is.
 */
export const widenCandidates = (
  investigation: Investigation,
  suggestions: InvestigationSuggestion[]
): InvestigationSuggestion[] => {
  const branches = caseRules(investigation);
  return suggestions
    .filter((s) => {
      const rule = s.derivedRule ?? [];
      return (
        !s.constraints?.length &&
        !s.messageConstraints?.length &&
        !s.approximate &&
        canWiden(investigation, rule) &&
        branches.some((branch) => ruleWithin(branch, rule))
      );
    })
    .sort((a, b) => (b.derivedRule?.length ?? 0) - (a.derivedRule?.length ?? 0))
    .slice(0, 1);
};

/** How many patterns the owner could widen this case to (the TrackStars tab's badge). */
export const widenActionCount = (
  investigation: Investigation,
  suggestions: InvestigationSuggestion[] | undefined
): number =>
  widenCandidates(
    investigation,
    (suggestions ?? []).filter((s) =>
      s.ownership.investigations.some(
        (inv) => inv.investigationId === investigation.id
      )
    )
  ).length;

type DiffRow = {
  field: string;
  before?: string;
  after?: string;
  change: "same" | "removed" | "added" | "changed";
};

/** Field-by-field comparison of a single-branch rule before and after widening. */
export const ruleDiffRows = (before: Rule, after: Rule): DiffRow[] => {
  const text = (rule: Rule) =>
    new Map(rule.map((p) => [p.field, predicateValueText(p)]));
  const a = text(before);
  const b = text(after);
  const fields = [...a.keys(), ...[...b.keys()].filter((f) => !a.has(f))];
  return fields.map((field) => {
    const was = a.get(field);
    const now = b.get(field);
    const change =
      was === now
        ? "same"
        : was === undefined
        ? "added"
        : now === undefined
        ? "removed"
        : "changed";
    return { field, before: was, after: now, change };
  });
};

const cellStyle: React.CSSProperties = {
  padding: "6px 8px",
  verticalAlign: "top",
  wordBreak: "break-word",
  fontFamily: "monospace",
  fontSize: "12px",
};

const RuleDiff: React.FC<{ before: Rule; after: Rule }> = ({
  before,
  after,
}) => (
  <table style={{ width: "100%", borderCollapse: "collapse" }}>
    <thead>
      <tr>
        <th style={{ textAlign: "left", width: "22%" }}>
          <Box variant="awsui-key-label">Field</Box>
        </th>
        <th style={{ textAlign: "left" }}>
          <Box variant="awsui-key-label">Rule now</Box>
        </th>
        <th style={{ textAlign: "left" }}>
          <Box variant="awsui-key-label">Rule after</Box>
        </th>
      </tr>
    </thead>
    <tbody>
      {ruleDiffRows(before, after).map((row) => (
        <tr
          key={row.field}
          data-testid={`rule-diff-${row.field}`}
          style={{ borderTop: "1px solid rgba(127,127,127,0.25)" }}
        >
          <td style={{ ...cellStyle, fontFamily: "inherit" }}>
            {ruleFieldLabel(row.field)}
          </td>
          <td
            style={{
              ...cellStyle,
              background:
                row.change === "removed" || row.change === "changed"
                  ? "rgba(209, 50, 18, 0.12)"
                  : undefined,
              textDecoration:
                row.change === "removed" ? "line-through" : undefined,
            }}
          >
            {row.before ?? "—"}
          </td>
          <td
            style={{
              ...cellStyle,
              background:
                row.change === "added" || row.change === "changed"
                  ? "rgba(0, 133, 55, 0.12)"
                  : undefined,
            }}
          >
            {row.after ?? (row.change === "removed" ? "any" : "—")}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const PROMPT_KEY = "lusca.widenPromptDismissed";

/** The page-top "TrackStars found more of this failure" prompt, dismissed per investigation. */
export const widenPromptWasDismissed = (investigationId?: string): boolean => {
  if (!investigationId) return false;
  try {
    const ids = JSON.parse(
      localStorage.getItem(PROMPT_KEY) || "[]"
    ) as string[];
    return ids.includes(investigationId);
  } catch {
    return false;
  }
};

export const rememberWidenPromptDismissed = (investigationId: string) => {
  try {
    const ids = JSON.parse(
      localStorage.getItem(PROMPT_KEY) || "[]"
    ) as string[];
    localStorage.setItem(
      PROMPT_KEY,
      JSON.stringify(
        [...ids.filter((i) => i !== investigationId), investigationId].slice(
          -200
        )
      )
    );
  } catch {
    // Storage unavailable: the prompt simply shows again next time.
  }
};

interface Props {
  stage: Stage;
  investigation: Investigation;
  rule: Rule;
  /** What the rule is, for the header ("TrackStars PAT-001"). */
  label: string;
  patternId?: string;
  /** The pattern's TrackStars category, which names a regenerated title. */
  category?: string | null;
  visible: boolean;
  onDismiss: () => void;
}

/** One confirmation: the rule now, the rule after, and what that does. Nothing else. */
export const WidenRuleModal: React.FC<Props> = ({
  stage,
  investigation,
  rule,
  label,
  patternId,
  category,
  visible,
  onDismiss,
}) => {
  const widen = useWidenRule(stage);
  const current = caseRules(investigation);
  const after = widenedRules(current, rule);
  const title = widenedTitle(investigation, after, category);
  const confirm = () => {
    widen
      .mutateAsync({
        investigationId: investigation.id,
        rule,
        patternId,
        title,
      })
      .then(onDismiss)
      .catch(() => undefined); // shown below
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header={`Widen the rule to cover ${label}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={widen.isLoading}
              onClick={confirm}
            >
              Widen the rule
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box>
          Unowned DLQ items matching the wider rule join this investigation
          automatically, within a few minutes, and new ones keep arriving. Items
          other cases own stay with them.
        </Box>
        {current.length === 1 && after.length === 1 ? (
          <RuleDiff before={current[0]} after={after[0]} />
        ) : (
          <SpaceBetween size="m">
            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">Rule now</Box>
              <InvestigationRule rules={current} compact />
            </SpaceBetween>
            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">Rule after</Box>
              <InvestigationRule rules={after} compact />
            </SpaceBetween>
          </SpaceBetween>
        )}
        {title && (
          <Box data-testid="widen-new-title">
            Title becomes <strong>{title}</strong>
          </Box>
        )}
        {widen.error && (
          <Alert type="error">
            {widen.error.message ?? "Could not widen the rule."}
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default WidenRuleModal;
